import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { PROMO_PARAM_KEY } from '../../Constants/promotions';
import { ROUTES, RouteName } from '../../Constants/routes';
import { MigrationTrigger, PromotionType } from '../../api/remoteConfigApi/remoteConfigApi.types';
import { useEnv } from '../../components/EnvProvider/EnvProvider';
import { useMigrationModal } from '../../components/Modals/MigratiionModal/hooks/useMigrationModal';
import { activePromotionsSelector } from '../../store/config';
import { previousLocationSelector } from '../../store/router/router.selectors';
import { useExtendedNavigate } from '../useExtendedNavigate';

export const usePromotionUnauthorized = (pageReady: boolean) => {
  const activePromotions = useSelector(activePromotionsSelector);
  const [showModal, setShowModal] = useState(false);
  const [promotionUrl, setPromotionUrl] = useState('');

  const navigate = useExtendedNavigate();
  const [searchParams] = useSearchParams();
  const { RESTRICT_TO_SUBSCRIPTION_ONLY } = useEnv();

  const previousLocation = useSelector(previousLocationSelector);
  const { migration } = useMigrationModal(MigrationTrigger.blockPurchase);

  const promotionIdParam = searchParams.get(PROMO_PARAM_KEY.promotionId);
  const couponParam = searchParams.get(PROMO_PARAM_KEY.coupon);
  const ppgIdParam = searchParams.get(PROMO_PARAM_KEY.ppgId);

  const promo = activePromotions.find((p) => p.id === promotionIdParam);

  useEffect(() => {
    if (!promo) {
      return;
    }

    if (promo.collectionId) {
      const urlParams = [
        `${PROMO_PARAM_KEY.promotionId}=${promo.id}`,
        `${PROMO_PARAM_KEY.id}=${promo.collectionId}`,
      ];

      setPromotionUrl(`${ROUTES[RouteName.Promotion]}?${urlParams.join('&')}`);
      return;
    }

    if (promo.zoneToPpgConfigurations) {
      const urlParams = [
        `${PROMO_PARAM_KEY.promotionId}=${promo.id}`,
        `${PROMO_PARAM_KEY.ppgId}=${ppgIdParam}`,
        `${PROMO_PARAM_KEY.coupon}=${couponParam}`,
      ];

      setPromotionUrl(`${ROUTES[RouteName.Home]}?${urlParams.join('&')}`);
      return;
    }

    const urlParams = [
      `${PROMO_PARAM_KEY.promotionId}=${promo.id}`,
      `${PROMO_PARAM_KEY.coupon}=${couponParam}`,
    ];

    setPromotionUrl(`${ROUTES[RouteName.Home]}?${urlParams.join('&')}`);
  }, [promo, ppgIdParam]);

  const updateUrlParamsBasedOnPrevPage = useCallback(() => {
    if (promo) {
      return;
    }

    const prevPageSearchParams = new URLSearchParams(previousLocation?.search);
    const prevPagePromotionId = prevPageSearchParams.get(PROMO_PARAM_KEY.promotionId) || '';
    const prevPageId = prevPageSearchParams.get(PROMO_PARAM_KEY.id) || '';

    if (!prevPagePromotionId && !prevPageId) {
      return;
    }

    let updatedSearchString = '';

    if (prevPagePromotionId) {
      updatedSearchString += `?${PROMO_PARAM_KEY.promotionId}=${prevPagePromotionId}`;
    } else if (prevPageId) {
      updatedSearchString += `?${PROMO_PARAM_KEY.promotionId}=${prevPageId}`;
    } else {
      return;
    }

    const prevPageCoupon = prevPageSearchParams.get(PROMO_PARAM_KEY.coupon) || '';
    const prevPagePpgId = prevPageSearchParams.get(PROMO_PARAM_KEY.ppgId) || '';

    if (prevPageCoupon) {
      updatedSearchString += `&${PROMO_PARAM_KEY.coupon}=${prevPageCoupon}`;
    }

    if (prevPagePpgId) {
      updatedSearchString += `&${PROMO_PARAM_KEY.ppgId}=${prevPagePpgId}`;
    }

    const currentPath = window.location.pathname + updatedSearchString;

    navigate(currentPath, { replace: true });
  }, [navigate, previousLocation?.search]);

  const customSteps = useMemo(() => {
    if (!promo?.zoneToPpgConfigurations) {
      return;
    }
    const zoneToPpgConfig = promo?.zoneToPpgConfigurations?.find((c) => c.id === ppgIdParam);

    if (!zoneToPpgConfig) {
      return;
    }

    const steps = [...promo.steps];

    if (steps[1] && zoneToPpgConfig?.gameImgSrc) {
      steps[1] = { ...steps[1], imgSrc: zoneToPpgConfig?.gameImgSrc };
    }

    return steps;
  }, [promo, ppgIdParam]);

  useEffect(() => {
    if (!pageReady || migration?.isEnabled) {
      return;
    }

    updateUrlParamsBasedOnPrevPage();

    if (!promo) {
      return;
    }

    // disable promotions when ppg purchase is blocked
    if (
      [PromotionType.FANDUEL_COLLECTION, PromotionType.ZONE_TO_PPG].includes(promo.type) &&
      RESTRICT_TO_SUBSCRIPTION_ONLY
    ) {
      return;
    }

    if (
      [ROUTES[RouteName.Promotion], ROUTES[RouteName.Home], undefined].includes(
        previousLocation?.pathname,
      )
    ) {
      setShowModal(true);
    }
  }, [promo, pageReady, previousLocation?.pathname, updateUrlParamsBasedOnPrevPage]);

  return {
    customSteps,
    promotion: promo,
    promotionUrl,
    showPromotionModal: showModal,
    skipFavoriteTeams: !!promo?.skipTeamSelection,
  };
};
